import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getOrgTeamMembers, upgradeMemberToAdmin, demoteAdmin, demoteTeamMemberToMember } from '../services/authService';
import './Team.css';

const Team = () => {
  const { currentUser, orgDomain, isOrgAdminUser } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  const loadTeamMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const members = await getOrgTeamMembers(orgDomain);
      setTeamMembers(members);
    } catch (err) {
      console.error('Error loading team members:', err);
      setError('Failed to load team members. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [orgDomain]);

  useEffect(() => {
    if (orgDomain && isOrgAdminUser) {
      loadTeamMembers();
    }
  }, [orgDomain, isOrgAdminUser, loadTeamMembers]);

  // Check if user is admin (after hooks)
  if (!isOrgAdminUser) {
    return (
      <div className="team-page">
        <div className="page-header">
          <h1>Team</h1>
        </div>
        <div className="error-state">
          <p>You must be an admin to view the team.</p>
        </div>
      </div>
    );
  }

  const handleUpgradeToAdmin = async (userId, email) => {
    if (!window.confirm(`Upgrade ${email} to admin?`)) {
      return;
    }

    try {
      setProcessingId(userId);
      await upgradeMemberToAdmin(userId, orgDomain);
      await loadTeamMembers(); // Reload team
    } catch (err) {
      console.error('Error upgrading member to admin:', err);
      alert('Failed to upgrade member. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRemoveMember = async (userId, email, isAdmin) => {
    if (isAdmin) {
      // Demote admin - ask if they want to keep as team member
      const keepAsTeamMember = window.confirm(
        `Demote ${email} from admin? They will lose admin privileges.\n\n` +
        `Click OK to keep them as team member, or Cancel to demote to regular member.`
      );
      
      if (!window.confirm(`Are you sure you want to demote ${email}?`)) {
        return;
      }

      try {
        setProcessingId(userId);
        await demoteAdmin(userId, orgDomain, keepAsTeamMember);
        await loadTeamMembers(); // Reload team
        alert(`${email} has been demoted ${keepAsTeamMember ? 'to team member' : 'to regular member'}.`);
      } catch (err) {
        console.error('Error demoting admin:', err);
        const errorMessage = err.message || 'Failed to demote admin. Please try again.';
        alert(errorMessage);
      } finally {
        setProcessingId(null);
      }
    } else {
      // Demote team member to regular member
      if (!window.confirm(`Demote ${email} from team member? They will lose admin panel access but remain a member.`)) {
        return;
      }

      try {
        setProcessingId(userId);
        await demoteTeamMemberToMember(userId, orgDomain);
        await loadTeamMembers(); // Reload team
        alert(`${email} has been demoted to regular member.`);
      } catch (err) {
        console.error('Error demoting team member:', err);
        const errorMessage = err.message || 'Failed to demote team member. Please try again.';
        alert(errorMessage);
      } finally {
        setProcessingId(null);
      }
    }
  };

  if (loading) {
    return (
      <div className="team-page">
        <div className="page-header">
          <h1>Team</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading team members...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="team-page">
        <div className="page-header">
          <h1>Team</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadTeamMembers} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const admins = teamMembers.filter((member) => member.isAdmin);
  const members = teamMembers.filter((member) => !member.isAdmin);

  return (
    <div className="team-page">
      <div className="page-header">
        <div>
          <h1>Team</h1>
          {orgDomain && <p className="org-domain">Domain: {orgDomain}</p>}
        </div>
        <button onClick={loadTeamMembers} className="refresh-button">
          🔄 Refresh
        </button>
      </div>

      <div className="team-stats">
        <div className="stat-card">
          <div className="stat-value">{teamMembers.length}</div>
          <div className="stat-label">Total Members</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{admins.length}</div>
          <div className="stat-label">Admins</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{members.length}</div>
          <div className="stat-label">Members</div>
        </div>
      </div>

      {teamMembers.length === 0 ? (
        <div className="empty-state">
          <p>No team members found</p>
        </div>
      ) : (
        <div className="team-sections">
          {admins.length > 0 && (
            <div className="team-section">
              <h2 className="section-title">Admins</h2>
              <div className="team-list">
                {admins.map((member) => (
                  <TeamMemberCard
                    key={member.userId}
                    member={member}
                    currentUserId={currentUser?.uid}
                    onUpgrade={handleUpgradeToAdmin}
                    onRemove={handleRemoveMember}
                    processingId={processingId}
                  />
                ))}
              </div>
            </div>
          )}

          {members.length > 0 && (
            <div className="team-section">
              <h2 className="section-title">Members</h2>
              <div className="team-list">
                {members.map((member) => (
                  <TeamMemberCard
                    key={member.userId}
                    member={member}
                    currentUserId={currentUser?.uid}
                    onUpgrade={handleUpgradeToAdmin}
                    onRemove={handleRemoveMember}
                    processingId={processingId}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TeamMemberCard = ({ member, currentUserId, onUpgrade, onRemove, processingId }) => {
  const isCurrentUser = member.userId === currentUserId;
  const isProcessing = processingId === member.userId;

  return (
    <div className="team-member-card">
      <div className="member-info">
        <div className="member-avatar">
          {member.photoURL && (
            <img 
              src={member.photoURL} 
              alt={member.name}
              className="avatar-image"
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = e.target.nextElementSibling;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
          )}
          <span className="avatar-fallback" style={{ display: member.photoURL ? 'none' : 'flex' }}>
            {member.name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="member-details">
          <div className="member-name">
            {member.name}
            {member.isAdmin && <span className="admin-badge">Admin</span>}
            {isCurrentUser && <span className="you-badge">You</span>}
          </div>
          <div className="member-email">{member.email}</div>
        </div>
      </div>
      <div className="member-actions">
        {!isCurrentUser && (
          <>
            {!member.isAdmin && (
              <button
                className="action-button upgrade-button"
                onClick={() => onUpgrade(member.userId, member.email)}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Make Admin'}
              </button>
            )}
            <button
              className="action-button remove-button"
              onClick={() => onRemove(member.userId, member.email, member.isAdmin)}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : member.isAdmin ? 'Demote Admin' : 'Demote to Member'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Team;
